import { useState, useEffect } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import api from '../../core/services/api'
import { GANTT_COLORS } from './ganttUtils'

interface ProgressPoint { data: string; rimanenti: number; lavorati: number }
interface CommessaBurndown {
  commessa_id: string
  numero_commessa: string
  titolo: string
  total_giorni: number
  data_inizio: string
  data_fine_prevista: string | null
  data_prevista_consegna: string | null
  progress: ProgressPoint[]
}

export default function BurndownCommesse() {
  const [data, setData] = useState<CommessaBurndown[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  useEffect(() => {
    api.get('/v1/strategia/burndown')
      .then((r: { data: CommessaBurndown[] }) => {
        setData(r.data)
        setSelected(new Set(r.data.map(c => c.commessa_id)))
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin" /></div>
  if (data.length === 0) return <div className="text-center text-gray-400 py-16">Nessuna commessa in esecuzione con dati sufficienti</div>

  // Costruisci dataset unificato per recharts: merge tutte le date
  const allDates = [...new Set(data.flatMap(c => c.progress.map(p => p.data)))].sort()
  const today = new Date().toISOString().split('T')[0]

  // Per ogni commessa, crea una linea ideale e una effettiva
  // Il grafico mostra % rimanente (0 = completato, 100 = appena iniziato)
  const chartData = allDates.map(dt => {
    const point: Record<string, unknown> = { data: dt.slice(5) } // MM-DD
    data.forEach((c) => {
      if (!selected.has(c.commessa_id)) return
      const prog = c.progress.find(p => p.data === dt)
      if (prog) {
        const pct = Math.round(prog.rimanenti / c.total_giorni * 100)
        point[`${c.numero_commessa}_eff`] = pct
      }
      // Linea ideale: interpolata linearmente da data_inizio (100%) a data_fine_prevista (0%)
      if (c.data_fine_prevista) {
        const start = new Date(c.data_inizio + 'T00:00:00').getTime()
        const end = new Date(c.data_fine_prevista + 'T00:00:00').getTime()
        const cur = new Date(dt + 'T00:00:00').getTime()
        if (cur >= start && cur <= end) {
          const ideal = Math.round(100 - ((cur - start) / (end - start)) * 100)
          point[`${c.numero_commessa}_ideal`] = ideal
        }
      }
    })
    return point
  })

  const toggle = (id: string) => setSelected(prev => {
    const n = new Set(prev)
    n.has(id) ? n.delete(id) : n.add(id)
    return n
  })

  return (
    <div className="space-y-4">
      {/* Selector */}
      <div className="flex flex-wrap gap-2">
        {data.map((c, ci) => {
          const color = GANTT_COLORS[ci % GANTT_COLORS.length]
          const active = selected.has(c.commessa_id)
          return (
            <button
              key={c.commessa_id}
              onClick={() => toggle(c.commessa_id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                active ? 'opacity-100' : 'opacity-40'
              }`}
              style={{ borderColor: color.bar, background: active ? color.light : 'white', color: color.text }}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: color.bar }} />
              {c.numero_commessa} — {c.titolo}
            </button>
          )
        })}
      </div>

      {/* Chart */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700">Giorni rimanenti nel tempo (%)</h3>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><span className="w-6 border-t-2 border-dashed border-gray-400 inline-block" /> Ideale</span>
            <span className="flex items-center gap-1"><span className="w-6 border-t-2 border-blue-500 inline-block" /> Effettivo</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={380}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="data" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v: unknown) => [`${v}%`]} />
            <ReferenceLine x={today.slice(5)} stroke="#ef4444" strokeDasharray="4 2" label={{ value: 'Oggi', fontSize: 10, fill: '#ef4444' }} />
            {data.map((c, ci) => {
              if (!selected.has(c.commessa_id)) return null
              const color = GANTT_COLORS[ci % GANTT_COLORS.length]
              return [
                <Line key={`${c.numero_commessa}_eff`} type="monotone" dataKey={`${c.numero_commessa}_eff`} stroke={color.bar} strokeWidth={2} dot={false} name={`${c.numero_commessa} effettivo`} connectNulls />,
                <Line key={`${c.numero_commessa}_ideal`} type="monotone" dataKey={`${c.numero_commessa}_ideal`} stroke={color.bar} strokeWidth={1.5} strokeDasharray="5 3" dot={false} name={`${c.numero_commessa} ideale`} connectNulls />,
              ]
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Cards di riepilogo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {data.filter(c => selected.has(c.commessa_id)).map((c, ci) => {
          const color = GANTT_COLORS[ci % GANTT_COLORS.length]
          const last = c.progress[c.progress.length - 1]
          const pct_done = last ? Math.round(last.lavorati / c.total_giorni * 100) : 0
          const in_ritardo = c.data_fine_prevista && c.data_prevista_consegna && c.data_fine_prevista > c.data_prevista_consegna
          return (
            <div key={c.commessa_id} className="border rounded-lg p-3 bg-white" style={{ borderLeftWidth: 3, borderLeftColor: color.bar }}>
              <p className="font-mono text-xs font-bold" style={{ color: color.text }}>{c.numero_commessa}</p>
              <p className="text-sm font-medium text-gray-700 truncate">{c.titolo}</p>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-gray-500">{pct_done}% completato</span>
                {in_ritardo && <span className="text-red-500 font-medium">⚠ Ritardo</span>}
              </div>
              <div className="mt-1 h-1.5 bg-gray-100 rounded-full">
                <div className="h-full rounded-full" style={{ width: `${pct_done}%`, background: color.bar }} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
